import { QueryKeys } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { ICollectableCoupon, ICouponHistory, ICollectableCouponSetting } from '@/interfaces/collectableCoupon';
import { collectCoupon, getApplicableCoupons, getAvailableCollectableCoupons, getCollectableCouponHistory, getDisplayDuration } from '@/services/collectableCoupon.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReactQuerySession } from '@/hooks/reactQuerySession';

export const useCollectableCouponsQuery = () => {
    const { session } = useReactQuerySession();

    return useQuery<ICollectableCoupon[]>({
        queryKey: [QueryKeys.collectableCoupons, session?.accessToken],
        queryFn: async () => {
            return await getAvailableCollectableCoupons(session);
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        retry: false,
    });
};

export const useCollectableCouponHistoryQuery = () => {
    const { session } = useReactQuerySession();

    return useQuery<ICouponHistory[]>({
        queryKey: [QueryKeys.myCoupons, session?.accessToken],
        queryFn: async () => {
            return await getCollectableCouponHistory(session);
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        retry: false,
    });
};

export const useApplicableCouponsQuery = () => {
    const { session } = useReactQuerySession();

    return useQuery<ICouponHistory[]>({
        queryKey: [QueryKeys.applicableCoupons, session?.accessToken],
        queryFn: async () => {
            return await getApplicableCoupons(session);
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
    });
};

export const useCollectCouponMutation = () => {
    const queryClient = useQueryClient();
    const { session } = useReactQuerySession();

    return useMutation({
        mutationFn: (couponId: string | number) => collectCoupon(couponId, session),
        onSuccess: () => {
            // Refetch the coupons list to remove the collected coupon, refresh
            // applicable-coupon counts, and resync My-Coupons history so any
            // coupon the user just collected flips to "Collected" immediately.
            queryClient.invalidateQueries({ queryKey: [QueryKeys.collectableCoupons] });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.applicableCoupons] });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.myCoupons] });
            openNotification('success', 'Coupon', 'Coupon collected successfully!');
        },
        onError: () => {
            openNotification('danger', 'Coupon', 'Failed to collect coupon. Please try again.');
        },
    });
};

export const useDisplayDurationQuery = () => {
    return useQuery<ICollectableCouponSetting | null>({
        queryKey: [QueryKeys.collectableCouponDisplayDuration],
        queryFn: async () => getDisplayDuration(),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
    });
};
